// Psychotherapy details — the browser's half of the answer codes.
//
// The single source of truth is server/src/services/vertraegeAnswers.ts (Justus's Make maps
// those exact keys); the server re-checks everything when the case is completed. This module
// only (a) tells the details step which fields are still missing, with the same rules, and
// (b) assembles the full answer set from the onboarding store, so nothing the onboarding
// already asked is asked twice.
import { countryIso } from '../data/countries'
import { GERMAN_PRIVATE_INSURERS } from '../data/insurers'

export type PtUploadKind = 'ptv11' | 'absageprotokoll'

/** Krankenversichertennummer: one capital letter + 9 digits (hard rule). */
export const KVNR_PATTERN = /^[A-Z]\d{9}$/

/** The KVNR check digit — only a warning, never blocking (mirrors the server). */
export function kvnrChecksumOk(kvnr: string): boolean {
  if (!KVNR_PATTERN.test(kvnr)) return false
  const letter = String(kvnr.charCodeAt(0) - 64).padStart(2, '0')
  const digits = (letter + kvnr.slice(1, 9)).split('').map(Number)
  const sum = digits.reduce((acc, d, i) => {
    const p = d * (i % 2 === 0 ? 1 : 2)
    return acc + (p > 9 ? Math.floor(p / 10) + (p % 10) : p)
  }, 0)
  return sum % 10 === Number(kvnr[9])
}

const filled = (v: unknown): boolean => v !== undefined && v !== null && !(typeof v === 'string' && v.trim() === '')

function todayBerlin(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
}

/**
 * The fields of the details step that are still missing or invalid (field codes;
 * upload_ptv11 / upload_absageprotokoll for a "yes" without a file). Only statutory insured
 * patients have required fields here.
 */
export function ptDetailsMissing(
  a: Record<string, unknown>,
  insuranceType: string,
  uploads: Partial<Record<PtUploadKind, number>>,
  now: Date = new Date(),
): string[] {
  if (insuranceType !== 'public') return []
  const missing: string[] = []
  const today = todayBerlin(now)
  const need = (k: string) => { if (!filled(a[k])) missing.push(k) }
  const notFuture = (k: string) => { if (filled(a[k]) && String(a[k]) > today) missing.push(k) }

  need('insurance_number')
  if (filled(a.insurance_number) && !KVNR_PATTERN.test(String(a.insurance_number))) missing.push('insurance_number')
  need('insured_status')
  if (a.insured_status === 'familienversichert') need('main_insured_name')
  need('has_ptv11')
  if (a.has_ptv11 === true) {
    need('ptv11_date'); notFuture('ptv11_date'); need('ptv11_urgent')
    if (!uploads.ptv11) missing.push('upload_ptv11')
  }
  need('tss_called')
  if (a.tss_called === true) {
    need('tss_date'); notFuture('tss_date'); need('tss_result')
    if (a.tss_result === 'waiting_over_x_weeks') need('tss_wait_weeks')
  }
  need('has_absagen')
  if (a.has_absagen === true) {
    need('absagen_count'); need('absagen_from'); need('absagen_to')
    notFuture('absagen_from'); notFuture('absagen_to')
    if (filled(a.absagen_from) && filled(a.absagen_to) && String(a.absagen_from) > String(a.absagen_to)) missing.push('absagen_to')
    if (typeof a.absagen_count === 'number' && a.absagen_count < 1) missing.push('absagen_count')
    if (!uploads.absageprotokoll) missing.push('upload_absageprotokoll')
  }
  need('has_konsil')
  need('gp_name'); need('gp_practice'); need('gp_city')
  need('prior_therapy')
  if (a.prior_therapy === true) need('prior_therapy_details')
  need('psychiatric_care')
  if (a.psychiatric_care === true) need('psychiatric_care_details')
  // Dok. 02 Ziffer 4 — preselected, so only missing if the patient cleared it
  if (a.prob_choice !== 'after_approval' && a.prob_choice !== 'self_pay_now') missing.push('prob_choice')
  return [...new Set(missing)]
}

interface StoreSlice {
  personalData: { firstName: string; lastName: string; birthDate: string; gender: string; email: string }
  phoneNumber: string
  addressData: { street?: string; houseNumber?: string; address: string; postalCode: string; city: string; country: string }
  insuranceData: { insuranceType: string; insuranceProvider: string; insuranceProviderOther?: string }
  ptAnswers: Record<string, unknown>
}

/** The Kasse as Justus's list knows it: the picked insurer, or the free text behind "Other". */
export function kasseOf(insurance: StoreSlice['insuranceData']): string {
  return insurance.insuranceProvider === 'Other' ? (insurance.insuranceProviderOther ?? '').trim() : insurance.insuranceProvider.trim()
}

/**
 * Every answer code for /api/vertraege/complete: the master data from the onboarding steps, the
 * Kasse from the insurance step, the psychotherapy details, and the payment method. Without the
 * payment method (not asked yet) it is what the documents print, for /api/yousign/create.
 */
export function buildPtAnswers(store: StoreSlice, paymentMethod?: 'sepa' | 'rechnung'): Record<string, unknown> {
  const { personalData: p, addressData: a, insuranceData: ins } = store
  // Older addresses were stored as one line; the street step splits new ones
  const street = (a.street ?? '').trim() || a.address.replace(/\s+\S*\d\S*$/, '').trim()
  const house = (a.houseNumber ?? '').trim() || (a.address.match(/(\S*\d\S*)$/)?.[1] ?? '')
  const out: Record<string, unknown> = {
    first_name: p.firstName.trim(),
    last_name: p.lastName.trim(),
    dateofbirth: p.birthDate,
    gender: p.gender || undefined,
    street,
    house_number: house,
    zip_code: a.postalCode.trim(),
    city: a.city.trim(),
    country: countryIso(a.country) ?? undefined,
    email: p.email.trim(),
    phone: store.phoneNumber.trim(),
    ...store.ptAnswers,
    payment_method: paymentMethod,
  }
  // The Kostenträger on Dok. 01. Statutory: the list value Justus's Make maps (Booking receives it).
  // Private: printed only — the full name ("AXA Krankenversicherung", not the list value "AXA").
  if (ins.insuranceType === 'public') out.insurance_name = kasseOf(ins)
  if (ins.insuranceType === 'private') {
    const value = kasseOf(ins)
    out.insurance_name = GERMAN_PRIVATE_INSURERS.find(i => i.value === value)?.label ?? value
  }
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined && v !== ''))
}
